const HELP = [
  // Pages
  {
    id: 'dashboard',
    term: 'Executive Summary (Dashboard)',
    category: 'Pages',
    definition: 'The landing page showing company-wide financial KPIs across all active construction projects. Provides a high-level snapshot of revenue, costs, gross profit, backlog, and the WIP billing position.',
  },
  {
    id: 'wip-report',
    term: 'WIP Report',
    category: 'Pages',
    definition: 'The Work in Progress Schedule — the core financial statement for a construction company. Shows every active project\'s contract values, earned revenue, costs, billings, and over/under billing position. This is the report shown to banks, sureties, and investors.',
  },
  {
    id: 'completed-jobs',
    term: 'Completed Jobs',
    category: 'Pages',
    definition: 'Projects that have moved to Warranty status in Procore. Shows final revenue, costs, and gross profit for completed contracts.',
  },

  // Report Tabs
  {
    id: 'wip-schedule-tab',
    term: 'WIP Schedule Tab',
    category: 'Report Tabs',
    definition: 'The full WIP table with all standard columns: Contract Values (A/B/C), Current Year, Prior Years, From Inception to Date (D through I), and At Period End (J/K). This is the format banks and CPAs expect to see.',
  },
  {
    id: 'risk-exposure-tab',
    term: 'Risk & Exposure Tab',
    category: 'Report Tabs',
    definition: 'Shows margin fade (erosion), remaining backlog, retainage held by owners, and change order exposure (approved, pending, draft). This tab highlights financial risk across the portfolio.',
  },
  {
    id: 'journal-entries-tab',
    term: 'Journal Entries Tab',
    category: 'Report Tabs',
    definition: 'Shows the GL adjustments needed to true up the balance sheet each period. Compares the current WIP position against the most recent snapshot and calculates the debit/credit entries for accounts 11200 (underbilled asset), 21000 (overbilled liability), and 42700 (revenue adjustment). Your accounting team uses these numbers to book WIP adjustments in NetSuite.',
  },

  // WIP Columns — Contract Values
  {
    id: 'col-a',
    term: 'Column A — Estimated Contract Revenue',
    category: 'WIP Columns',
    definition: 'The total expected revenue for the project, including the original contract plus all approved change orders. Sourced from the Procore Revised Budget (prime contract revised amount).',
  },
  {
    id: 'col-b',
    term: 'Column B — Projected Total Costs',
    category: 'WIP Columns',
    definition: 'The total expected cost to complete the project. Calculated from the sum of all commitment (subcontractor) contracts plus direct costs. This is what you expect to spend by the time the project is done.',
  },
  {
    id: 'col-c',
    term: 'Column C — Estimated Gross Profit',
    category: 'WIP Columns',
    definition: 'The expected profit on the project. Calculated as Column A minus Column B (Revenue minus Costs). A negative number means the project is expected to lose money.',
  },
  {
    id: 'gp-pct',
    term: 'GP % (Gross Profit Percentage)',
    category: 'WIP Columns',
    definition: 'Gross profit as a percentage of revenue. Calculated as Estimated Gross Profit divided by Estimated Contract Revenue. For example, 15% means you keep $0.15 of every $1.00 of revenue after direct project costs.',
  },

  // WIP Columns — CY/PY
  {
    id: 'cy-revenue',
    term: 'Current Year Earned Revenue',
    category: 'WIP Columns',
    definition: 'Revenue billed to the owner during the current fiscal year (since April 1). Sourced from Procore owner invoices with billing dates in the current fiscal year.',
  },
  {
    id: 'cy-costs',
    term: 'Current Year Contract Costs',
    category: 'WIP Columns',
    definition: 'Costs incurred during the current fiscal year (since April 1). Includes subcontractor invoices and direct costs with dates in the current fiscal year.',
  },
  {
    id: 'py-revenue',
    term: 'Prior Years Earned Revenue',
    category: 'WIP Columns',
    definition: 'Revenue billed to the owner before the current fiscal year started (before April 1). This represents work that was completed and billed in previous fiscal years.',
  },

  // WIP Columns — Inception
  {
    id: 'col-d',
    term: 'Column D — Earned Contract Revenue (Inception)',
    category: 'WIP Columns',
    definition: 'The revenue you have "earned" based on how much of the project is complete. Calculated as Percentage Complete multiplied by Estimated Contract Revenue. This may differ from what you\'ve actually billed — that difference is the over/under billing.',
  },
  {
    id: 'col-e',
    term: 'Column E — Contract Costs to Date',
    category: 'WIP Columns',
    definition: 'Total actual costs incurred on the project from inception through today. Includes all subcontractor invoices paid plus direct costs. This is what you\'ve actually spent.',
  },
  {
    id: 'col-g',
    term: 'Column G — Contract Billings',
    category: 'WIP Columns',
    definition: 'Total amount billed to the owner from inception through today. This is what you\'ve invoiced — not necessarily what you\'ve collected (the difference is accounts receivable).',
  },
  {
    id: 'col-h',
    term: 'Column H — Estimated Costs to Complete',
    category: 'WIP Columns',
    definition: 'How much more you expect to spend to finish the project. Calculated as Projected Total Costs minus Costs to Date. Zero means the project costs are fully incurred.',
  },
  {
    id: 'col-i',
    term: 'Column I — Percentage Complete',
    category: 'WIP Columns',
    definition: 'How far along the project is, measured by the cost-to-cost method. Calculated as Costs to Date divided by Projected Total Costs. For example, if you\'ve spent $800K of a projected $1M, you\'re 80% complete. This is the standard method under ASC 606 for construction contracts.',
  },

  // WIP Columns — Period End
  {
    id: 'col-j',
    term: 'Column J — Costs in Excess of Billings (Under-Billed)',
    category: 'WIP Columns',
    definition: 'When you\'ve earned more revenue than you\'ve billed. This is a balance sheet ASSET — you\'ve done the work but haven\'t invoiced for it yet. A large underbilled position means you\'re financing the owner\'s project with your own cash. Banks watch this number closely.',
  },
  {
    id: 'col-k',
    term: 'Column K — Billings in Excess of Costs (Over-Billed)',
    category: 'WIP Columns',
    definition: 'When you\'ve billed more than you\'ve earned. This is a balance sheet LIABILITY — the owner has paid you for work you haven\'t completed yet. A healthy construction company typically has a slight overbilled position (you\'re billing ahead of the work).',
  },

  // CFO Metrics
  {
    id: 'backlog',
    term: 'Backlog',
    category: 'Financial Metrics',
    definition: 'Remaining contracted revenue that hasn\'t been earned yet. Calculated as Estimated Contract Revenue minus Earned Contract Revenue. This is the work you have under contract but haven\'t completed. Banks and investors use backlog to assess future revenue visibility — a strong backlog means predictable future cash flow.',
  },
  {
    id: 'retainage',
    term: 'Retainage Held',
    category: 'Financial Metrics',
    definition: 'The amount withheld by the project owner from each payment until the project is substantially complete (typically 5-10%). You\'ve earned this money but can\'t collect it until the owner releases it. It\'s a balance sheet asset. Retainage can tie up significant cash in a construction company.',
  },
  {
    id: 'fade',
    term: 'Margin Fade',
    category: 'Financial Metrics',
    definition: 'The change in gross profit percentage from the original estimate to the current projection. A negative fade means the project\'s margin has eroded — costs are higher or revenue is lower than originally expected. Projects with significant negative fade are risk indicators that deserve management attention.',
  },
  {
    id: 'co-exposure',
    term: 'Change Order Exposure',
    category: 'Financial Metrics',
    definition: 'The total value of change orders that are pending or in draft status — revenue that may be contracted but isn\'t approved yet. Approved COs are already included in the contract value. Pending and draft COs represent potential upside (or risk if they\'re denied). A large exposure relative to contract value signals uncertainty.',
  },
  {
    id: 'net-wip',
    term: 'Net WIP Position',
    category: 'Financial Metrics',
    definition: 'The net of underbilled minus overbilled across all projects. A positive number means the company is net underbilled (an asset — you\'ve done more work than you\'ve billed). A negative number means the company is net overbilled (a liability — you\'ve billed ahead of the work). Banks use this to assess the company\'s true financial position beyond what the income statement shows.',
  },

  // Journal Entry Terms
  {
    id: 'je-11200',
    term: 'Account 11200 — Costs in Excess of Billings',
    category: 'Journal Entries',
    definition: 'A balance sheet asset account. When the underbilled position increases from last period, you debit (increase) this account. When it decreases, you credit (reduce) it. The offset goes to account 42700 (revenue).',
  },
  {
    id: 'je-21000',
    term: 'Account 21000 — Billings in Excess of Costs',
    category: 'Journal Entries',
    definition: 'A balance sheet liability account. When the overbilled position increases from last period, you credit (increase) this account. When it decreases, you debit (reduce) it. The offset goes to account 42700 (revenue).',
  },
  {
    id: 'je-42700',
    term: 'Account 42700 — WIP Revenue Adjustment',
    category: 'Journal Entries',
    definition: 'An income statement revenue account used to record the net WIP adjustment each period. This is the offsetting entry to the balance sheet changes in accounts 11200 and 21000. It ensures that revenue recognized on the income statement matches the percentage-of-completion calculation, not just what was billed.',
  },

  // System Terms
  {
    id: 'snapshot',
    term: 'WIP Snapshot',
    category: 'System',
    definition: 'A frozen copy of the entire WIP report at a specific point in time. Snapshots are captured weekly and store every project\'s financial position. They provide the "prior period" baseline for journal entry calculations and create an audit trail for historical reporting.',
  },
  {
    id: 'data-refresh',
    term: 'Data as of (Refresh Timestamp)',
    category: 'System',
    definition: 'Shows when the Procore data was last synced to the data warehouse. The pipeline runs every 3 hours. All financial data in Ledger comes from Procore — this timestamp tells you how current it is.',
  },
  {
    id: 'pct-complete-method',
    term: 'Percentage of Completion (Cost-to-Cost Method)',
    category: 'System',
    definition: 'The method used to recognize revenue on long-term construction contracts (per ASC 606). Progress is measured by dividing actual costs incurred by total estimated costs. If a project has spent $600K of an estimated $1M total cost, it\'s 60% complete, and 60% of the contract revenue is "earned" — regardless of how much has been billed.',
  },
  {
    id: 'fiscal-year',
    term: 'Fiscal Year',
    category: 'System',
    definition: 'CM Corp\'s fiscal year runs April 1 through March 31. FY2026 = April 1, 2026 to March 31, 2027. The Current Year and Prior Years columns in the WIP report split data at the April 1 boundary.',
  },
  {
    id: 'assume-identity',
    term: 'Assume Identity (View As)',
    category: 'System',
    definition: 'Allows admin and leadership users to view the application as another user. Useful for seeing what a specific Project Manager or SPX sees — their projects are filtered to only show their assignments.',
  },
];

export default HELP;

export function searchHelp(query) {
  if (!query || !query.trim()) return HELP;
  const q = query.toLowerCase();
  return HELP.filter(h =>
    h.term.toLowerCase().includes(q) ||
    h.definition.toLowerCase().includes(q) ||
    h.category.toLowerCase().includes(q)
  );
}

export function getHelpById(id) {
  return HELP.find(h => h.id === id);
}

export const CATEGORIES = [...new Set(HELP.map(h => h.category))];
